import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import { dispatch } from "../../../../state/store";
import {
  setFWUCampaignDetails,
  setFwuCampaignDetailsRefresh,
  setFWUCampaignToEdit
} from "../../../../state/ducks";

import { campaignStatus } from "../../../../consts";
import { metricsConfig } from "./consts/metricsConfig";
import { lablesInfoConfig } from "./consts/labelsInfoConfig";
import { formatUtils } from "./utils/formatUtils";

import { CustomModalContainer, Modal } from "../../../../components";
import { SplittedModalContentTemplate } from "../../../../templates";

import { Loader } from "../../components/Loader/Loader";
import { CampaignInfo } from "./components/CampaignInfo/CampaignInfo";
import { Settings } from "./components/Settings/Settings";
import { ReportChart } from "../../components/ReportChart/ReportChart";
import { FWUCampaignReportWrapper } from "./styled";

const FWUCampaignReportModal = ({
  active,
  fwuCampaignData,
  fwuCampaignDetails,
  fwuCampaignDetailsLoading,
  loading,
  error,
  setActive,
  onSave,
  onDeactivate
}) => {
  const [isBottomToggled, setIsBottomToggled] = useState(false);

  const isCampaignActive = useMemo(() => {
    return fwuCampaignData?.status === campaignStatus.active;
  }, [fwuCampaignData]);

  const settingsAvailable = useMemo(() => {
    return !!fwuCampaignData?.markets?.length && isCampaignActive;
  }, [fwuCampaignData, isCampaignActive]);

  const reportData = useMemo(() => {
    return formatUtils.formatFWUCampaignDetails(fwuCampaignDetails);
  }, [fwuCampaignDetails]);

  useEffect(() => {
    if (!loading && error) {
      setIsBottomToggled(true);
    }
  }, [loading, error]);

  const onApplyChanges = (settingsData) => {
    const formattedSettingsData = formatUtils.formatSettingsData(
      fwuCampaignData?.campaignName,
      settingsData
    );

    onSave(formattedSettingsData);

    setIsBottomToggled(false);
  };

  const onDeactivateCampaign = () => {
    setIsBottomToggled(false);
    onDeactivate(fwuCampaignData.campaignName);
  };

  const onClose = () => {
    dispatch(setFWUCampaignToEdit(null));
    dispatch(setFWUCampaignDetails(null));
    setIsBottomToggled(false);
    setActive(false);
  };

  const onRefreshChartData = () => {
    dispatch(setFwuCampaignDetailsRefresh(true));
  };

  return (
    <Modal
      id={"new-fwu-modal"}
      active={active}
      setActive={onClose}
      Container={CustomModalContainer}>
      <FWUCampaignReportWrapper isBottomToggled={isBottomToggled}>
        <SplittedModalContentTemplate
          title={fwuCampaignData?.campaignName}
          LeftSectionContent={
            <CampaignInfo
              fwuCampaignData={fwuCampaignData}
              loading={loading}
              onDeactivate={onDeactivateCampaign}
            />
          }
          BottomSectionContent={
            settingsAvailable ? (
              <Settings
                isToggled={isBottomToggled}
                fwuCampaignData={fwuCampaignData}
                disabled={loading}
                setIsToggled={() => setIsBottomToggled((prev) => !prev)}
                onApplyChanges={onApplyChanges}
              />
            ) : null
          }>
          {loading ? (
            <Loader />
          ) : (
            <ReportChart
              reportData={reportData}
              metricsConfig={metricsConfig}
              metricTotal={metricsConfig.updated.id}
              lablesInfoConfig={lablesInfoConfig}
              loading={fwuCampaignDetailsLoading}
              reportFileName={fwuCampaignData?.campaignName}
              onRefreshChartData={isCampaignActive ? onRefreshChartData : null}
            />
          )}
        </SplittedModalContentTemplate>
      </FWUCampaignReportWrapper>
    </Modal>
  );
};

FWUCampaignReportModal.propTypes = {
  active: PropTypes.bool.isRequired,

  fwuCampaignData: PropTypes.shape({
    campaignName: PropTypes.string.isRequired,
    deviceModel: PropTypes.string.isRequired,
    currentFirmwares: PropTypes.arrayOf(PropTypes.string),
    markets: PropTypes.arrayOf(PropTypes.string),
    device: PropTypes.string.isRequired,
    versionTo: PropTypes.string.isRequired,
    startDate: PropTypes.string.isRequired,
    deactivationDate: PropTypes.string,
    status: PropTypes.string.isRequired,
    numberOfSucceededThings: PropTypes.number.isRequired,
    numberOfFailedThings: PropTypes.number.isRequired,
    pushNotificationStartDate: PropTypes.number.isRequired
  }).isRequired,
  fwuCampaignDetails: PropTypes.shape({
    campaignName: PropTypes.string.isRequired,
    numberOfCanceledThings: PropTypes.number.isRequired,
    numberOfRemovedThings: PropTypes.number.isRequired,
    numberOfFailedThings: PropTypes.number.isRequired,
    numberOfRejectedThings: PropTypes.number.isRequired,
    numberOfQueuedThings: PropTypes.number.isRequired,
    numberOfSucceededThings: PropTypes.number.isRequired,
    numberOfInProgressThings: PropTypes.number.isRequired,
    numberOfNotAttemptedThings: PropTypes.number.isRequired
  }).isRequired,
  fwuCampaignDetailsLoading: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.bool.isRequired,
  setActive: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onDeactivate: PropTypes.func.isRequired
};

export default FWUCampaignReportModal;
